import {gsap} from 'gsap';

export default class ScrollEffect {
  constructor() {
    this.body = document.querySelector('body');
    this.container = document.querySelector('.js-scroll-effect');
    this.expandableDiv = document.querySelector('.js-expand-div');
    this.previewPause = document.querySelector('.js-pause-button');
    this.previewPlay = document.querySelector('.js-play-button');
    this.watchBtn = document.querySelector('.video-component__watch-btn');
    this.previewVideo = document.querySelector('.video-component__video');
    this.fullVideo = document.querySelector('.js-full-video');
    this.closeVideoBtn = document.querySelector('.js-close-video');
    this.youTubeIframeContainer = document.querySelector('.video-component__yt-iframe__container');
    this.youTubeIframeWrapper = document.querySelector('.video-component__yt-iframe__wrapper');
    this.closeYouTubeModalBtn = document.querySelector('.video-component__yt-iframe__close-modal');
    this.ytVideoId = null;

    if (this.youTubeIframeContainer) {
      this.ytVideoId = this.youTubeIframeContainer.dataset.ytId;
    }

    // Setup hover animations
    if (this.container.classList.contains('--use-hover-animation')) {
      this.container.addEventListener('mouseenter', this.animateExpand.bind(this));
      this.container.addEventListener('mouseleave', this.animateReset.bind(this));
    } else {
      this.animateExpand();
    }

    // Full video - locally hosted using video tag <video>
    if (this.container.classList.contains('--use-video') && this.fullVideo) {
      this.initVideo();
    }

    // Init YouTube iframe
    if (this.container.classList.contains('--use-video') && this.ytVideoId) {
      this.initYouTubeIframe();
    }
  }

  initYouTubeIframe() {
    // Pause preview video
    this.previewPause.addEventListener('click', () => {
      this.previewVideo.pause();
      this.togglePlayPauseButtons();
    });

    // Play preview video
    this.previewPlay.addEventListener('click', () => {
      this.previewVideo.play();
      this.togglePlayPauseButtons();
    });

    // Watch YouTube iFrame
    this.watchBtn.addEventListener('click', this.handleWatchYTIframe.bind(this));
    // Close YouTube iFrame
    this.closeYouTubeModalBtn.addEventListener('click', this.handleCloseYTIframe.bind(this));
  }

  handleWatchYTIframe() {
    const ytVideoId = this.ytVideoId = this.youTubeIframeContainer.dataset.ytId;
    if (ytVideoId) {
      this.previewVideo.pause();
      this.body.classList.add('--overflow-hidden');
      this.youTubeIframeContainer.classList.add('--active');
      this.youTubeIframeWrapper.innerHTML = `
      <iframe width="560" height="315" src="https://www.youtube.com/embed/${this.ytVideoId}?rel=0&autoplay=1" frameborder="0" allow="autoplay; accelerometer; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
    `;
    }
  }

  handleCloseYTIframe() {
    this.previewVideo.play();
    this.youTubeIframeWrapper.innerHTML = '';
    this.body.classList.remove('--overflow-hidden');
    this.youTubeIframeContainer.classList.remove('--active');
  }

  initVideo() {
    // Desktop controls
    this.desktopControls = document.querySelector('.video-component__controls-desktop');
    this.desktopFullVideoPause = this.desktopControls.querySelector('.video-component__full-video-pause');
    this.desktopFullVideoPlay = this.desktopControls.querySelector('.video-component__full-video-play');
    this.desktopRewindButton = this.desktopControls.querySelector('.video-component__rewind');
    this.desktopFastForwardButton = this.desktopControls.querySelector('.video-component__fast-forward');
    this.desktopClosedCaptionButton = this.desktopControls.querySelector('.video-component__closed-caption');
    this.desktopTimeDisplay = this.desktopControls.querySelector('.video-component__time');
    this.desktopVolumeContainer = this.desktopControls.querySelector('.video-component__volume');
    this.desktopVolumeBar = this.desktopControls.querySelector('.video-component__volume-bar');

    // Mobile controls
    this.mobileControls = document.querySelector('.video-component__controls-mobile');
    this.mobileFullVideoPause = this.mobileControls.querySelector('.video-component__full-video-pause');
    this.mobileFullVideoPlay = this.mobileControls.querySelector('.video-component__full-video-play');
    this.mobileRewindButton = this.mobileControls.querySelector('.video-component__rewind');
    this.mobileFastForwardButton = this.mobileControls.querySelector('.video-component__fast-forward');
    this.mobileClosedCaptionButton = this.mobileControls.querySelector('.video-component__closed-caption');
    this.mobileTimeDisplay = this.mobileControls.querySelector('.video-component__time');
    this.mobileVolumeContainer = this.mobileControls.querySelector('.video-component__volume');
    this.mobileVolumeBar = this.mobileControls.querySelector('.video-component__volume-bar');

    this.toggleClosedCaptions();
    this.initEventListeners();
  }

  initEventListeners() {
    // pause + play
    this.desktopFullVideoPause.addEventListener('click', this.togglePausePlay.bind(this, this.fullVideo));
    this.desktopFullVideoPlay.addEventListener('click', this.togglePausePlay.bind(this, this.fullVideo));
    this.mobileFullVideoPause.addEventListener('click', this.togglePausePlay.bind(this, this.fullVideo));
    this.mobileFullVideoPlay.addEventListener('click', this.togglePausePlay.bind(this, this.fullVideo));

    // fast forward + rewind
    this.desktopRewindButton.addEventListener('click', this.adjustPlaybackTime.bind(this, -5));
    this.desktopFastForwardButton.addEventListener('click', this.adjustPlaybackTime.bind(this, 5));
    this.mobileRewindButton.addEventListener('click', this.adjustPlaybackTime.bind(this, -5));
    this.mobileFastForwardButton.addEventListener('click', this.adjustPlaybackTime.bind(this, 5));

    // Closed Caption
    this.desktopClosedCaptionButton?.addEventListener('click', this.toggleClosedCaptions.bind(this));
    this.mobileClosedCaptionButton?.addEventListener('click', this.toggleClosedCaptions.bind(this));

    // Volume Controls
    this.desktopVolumeContainer.addEventListener('click', this.toggleVolumeBarClassDesktop.bind(this));
    this.mobileVolumeContainer.addEventListener('click', this.toggleVolumeBarClassMobile.bind(this));
    this.desktopVolumeBar.addEventListener('input', this.handleVolumeChangeDesktop.bind(this));
    this.mobileVolumeBar.addEventListener('input', this.handleVolumeChangeMobile.bind(this));

    // Single version works for both desktop and mobile. Layout changes led to desktop mobile video controls above
    this.previewPause.addEventListener('click', this.togglePausePlay.bind(this, this.previewVideo));
    this.previewPlay.addEventListener('click', this.togglePausePlay.bind(this, this.previewVideo));
    this.watchBtn.addEventListener('click', this.handleWatchClick.bind(this));
    this.closeVideoBtn.addEventListener('click', this.handleCloseVideo.bind(this));
    this.fullVideo.addEventListener('timeupdate', this.updateTimeDisplay.bind(this));
  }

  togglePausePlay(video) {
    if (!video.paused) {
      video.pause();
    } else {
      video.play();
    }
    this.togglePlayPauseButtons();
  }

  handleWatchClick() {
    this.expandFullVideo();
    gsap.to(this.expandableDiv, {padding: 0, width: '100%', maxWidth: '100%'});
  }

  handleCloseVideo() {
    this.closeFullVideo();

    if (this.container.classList.contains('--use-hover-animation')) {
      gsap.to(this.expandableDiv, {padding: '63px 40px', width: '100%', maxWidth: '1520px'});
    }
  }

  animateExpand() {
    if (!this.container.classList.contains('--full-video-active')) {
      gsap.to(this.expandableDiv, {padding: 0, width: '100%', maxWidth: '100%'});
    }
  }

  animateReset() {
    if (!this.container.classList.contains('--full-video-active')) {
      gsap.to(this.expandableDiv, {padding: '63px 40px', width: '100%', maxWidth: '1520px'});
    }
  }

  togglePlayPauseButtons() {
    if (this.previewVideo) {
      const isPreviewPaused = this.previewVideo.paused;
      this.previewPlay.style.display = isPreviewPaused ? 'block' : 'none';
      this.previewPause.style.display = isPreviewPaused ? 'none' : 'block';
    }

    if (this.fullVideo) {
      const isFullVideoPaused = this.fullVideo.paused;
      // For desktop controls
      this.desktopFullVideoPlay.style.display = isFullVideoPaused ? 'block' : 'none';
      this.desktopFullVideoPause.style.display = isFullVideoPaused ? 'none' : 'block';

      // For mobile controls
      this.mobileFullVideoPlay.style.display = isFullVideoPaused ? 'block' : 'none';
      this.mobileFullVideoPause.style.display = isFullVideoPaused ? 'none' : 'block';
    }

  }

  updateTimeDisplay() {
    const currentTime = this.fullVideo.currentTime;
    const duration = this.fullVideo.duration;
    const formattedTime = this.formatTime(currentTime) + ' / ' + this.formatTime(duration);
    this.desktopTimeDisplay.textContent = formattedTime;
    this.mobileTimeDisplay.textContent = formattedTime;
  }

  formatTime(time) {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  adjustPlaybackTime(offset) {
    const newTime = Math.max(0, Math.min(this.fullVideo.currentTime + offset, this.fullVideo.duration));
    this.updateTimeDisplay();
    this.fullVideo.currentTime = newTime;
  }

  toggleClosedCaptions() {
    if (this.fullVideo.textTracks.length > 0) {
      const captionsTrack = this.fullVideo.textTracks[0];
      captionsTrack.mode = (captionsTrack.mode === 'showing') ? 'hidden' : 'showing';
    }
  }

  toggleVolumeBarClassDesktop() {
    this.desktopVolumeBar.classList.toggle('--volume-active');
  }

  toggleVolumeBarClassMobile() {
    const isVolumeActive = this.mobileVolumeBar.classList.contains('--volume-active');

    if (isVolumeActive) {
      this.mobileVolumeBar.classList.remove('--volume-active');
    } else {
      this.mobileVolumeBar.classList.add('--volume-active');
    }
  }

  handleVolumeChangeDesktop() {
    const volumeDesktop = this.desktopVolumeBar.value;
    this.fullVideo.volume = volumeDesktop;
  }

  handleVolumeChangeMobile() {
    const volumeMobile = this.mobileVolumeBar.value;
    this.fullVideo.volume = volumeMobile;
  }

  expandFullVideo() {
    this.container.classList.add('--full-video-active');
    this.fullVideo.play();
    this.previewVideo.pause();
    this.previewPlay.style.display = 'block';
    this.previewPause.style.display = 'none';
    this.desktopFullVideoPlay.style.display = 'none';
    this.desktopFullVideoPause.style.display = 'block';
    this.mobileFullVideoPlay.style.display = 'none';
    this.mobileFullVideoPause.style.display = 'block';

    this.handleBillboardOverlap();
  }

  closeFullVideo() {
    this.container.classList.remove('--full-video-active');
    this.fullVideo.pause();
    this.previewVideo.play();
    this.previewPlay.style.display = 'none';
    this.previewPause.style.display = 'block';
    this.desktopFullVideoPlay.style.display = 'block';
    this.desktopFullVideoPause.style.display = 'none';
    this.mobileFullVideoPlay.style.display = 'block';
    this.mobileFullVideoPause.style.display = 'none';

    this.handleBillboardOverlap();
  }

  handleBillboardOverlap() {
    let billboardElement = this.container.parentElement.nextElementSibling;

    if (billboardElement?.classList.contains('billboard')) {
      if (this.container.classList.contains('--full-video-active')) {
        billboardElement.querySelector('.js-billboard-image')?.classList.remove('--overlap-image');
      } else {
        billboardElement.querySelector('.js-billboard-image')?.classList.add('--overlap-image');
      }
    }
  }
}
